import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { ScrollText, Eye, Pencil, Trash2, Printer, FileDown, FileCode2, Plus, Search, ShieldCheck, FileSpreadsheet } from "lucide-react";
import { jsPDF } from "jspdf";

const fade = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const ACTION_META = {
  ver: { icon: Eye, chip: "text-sky-700 bg-sky-50", label: "Ver" },
  abrir: { icon: Eye, chip: "text-sky-700 bg-sky-50", label: "Abrir" },
  editar: { icon: Pencil, chip: "text-amber-700 bg-amber-50", label: "Editar" },
  crear: { icon: Plus, chip: "text-emerald-700 bg-emerald-50", label: "Crear" },
  eliminar: { icon: Trash2, chip: "text-red-700 bg-red-50", label: "Eliminar" },
  imprimir: { icon: Printer, chip: "text-violet-700 bg-violet-50", label: "Imprimir" },
  exportar: { icon: FileDown, chip: "text-violet-700 bg-violet-50", label: "Exportar" },
  descargar: { icon: FileDown, chip: "text-violet-700 bg-violet-50", label: "Descargar" },
  exportar_cda: { icon: FileCode2, chip: "text-teal-700 bg-teal-50", label: "Exportar CDA" },
  importar_cda: { icon: FileCode2, chip: "text-teal-700 bg-teal-50", label: "Importar CDA" },
};

export default function Bitacora() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/access-log").then((r) => setLogs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = logs.filter((l) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [l.action, l.entity, l.detail, l.patient_id, l.ip].some((v) => (v || "").toLowerCase().includes(s));
  });

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("es-MX"); } catch { return iso; } };

  const exportCSV = () => {
    const rows = [["Fecha/Hora", "Acción", "Entidad", "Expediente", "Detalle", "Médico", "IP", "Navegador"]];
    filtered.forEach((l) => rows.push([
      fmt(l.at), l.action || "", l.entity || "", l.patient_id || "", l.detail || "", l.doctor_name || "", l.ip || "", l.user_agent || "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url; a.download = `bitacora_clinped_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ unit: "mm", format: "letter", compress: true });
    doc.setFontSize(15); doc.setTextColor(11, 18, 32);
    doc.text("Bitácora de accesos — ClinPed", 14, 16);
    doc.setFontSize(9); doc.setTextColor(120, 120, 120);
    doc.text(`NOM-024 · Generada ${new Date().toLocaleString("es-MX")} · ${filtered.length} registro(s)`, 14, 22);
    let y = 32;
    doc.setFontSize(8); doc.setTextColor(90, 90, 90);
    doc.text("Fecha/Hora", 14, y); doc.text("Acción", 62, y); doc.text("Entidad", 85, y); doc.text("Expediente", 112, y); doc.text("IP", 155, y);
    doc.setDrawColor(220, 220, 220); doc.line(14, y + 1.5, 202, y + 1.5); y += 6;
    doc.setTextColor(30, 30, 30);
    filtered.forEach((l) => {
      if (y > 268) { doc.addPage(); y = 20; }
      doc.text(fmt(l.at).slice(0, 22), 14, y);
      doc.text((l.action || "").slice(0, 12), 62, y);
      doc.text((l.entity || "").slice(0, 14), 85, y);
      doc.text((l.patient_id || "").slice(0, 22), 112, y);
      doc.text((l.ip || "—").slice(0, 22), 155, y);
      y += 5.5;
    });
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    if (/iP(hone|ad|od)|Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)) {
      window.open(url, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = url; a.download = `bitacora_clinped_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  };

  return (
    <div className="space-y-6" data-testid="bitacora-page">
      <motion.div {...fade}>
        <p className="text-brand-sage font-bold text-sm tracking-wide uppercase">NOM-024</p>
        <h1 className="font-outfit text-3xl sm:text-4xl font-extrabold text-brand-ink tracking-tight flex items-center gap-2">
          <ScrollText size={30} className="text-brand-terracotta" /> Bitácora de accesos
        </h1>
        <p className="text-muted-foreground mt-1">Registro de quién consulta, edita, imprime o exporta cada expediente, con IP y fecha/hora.</p>
      </motion.div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input data-testid="bitacora-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por acción, expediente o IP…"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-white border border-stone-200 focus:outline-none focus:ring-2 focus:ring-brand-terracotta/40" />
        </div>
        <button data-testid="bitacora-export-csv" onClick={exportCSV} disabled={!filtered.length}
          className="flex items-center gap-1.5 bg-white border border-stone-200 text-brand-ink text-sm font-semibold px-3.5 py-2.5 rounded-xl hover:bg-muted transition-all disabled:opacity-50">
          <FileSpreadsheet size={16} /> CSV
        </button>
        <button data-testid="bitacora-export-pdf" onClick={exportPDF} disabled={!filtered.length}
          className="flex items-center gap-1.5 bg-brand-ink text-white text-sm font-semibold px-3.5 py-2.5 rounded-xl hover:opacity-90 transition-all disabled:opacity-50">
          <FileDown size={16} /> PDF
        </button>
      </div>

      <motion.div {...fade} transition={{ delay: 0.08 }}
        className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(61,64,91,0.04)] border border-stone-100 overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><div className="w-8 h-8 rounded-full border-[3px] border-brand-sage/30 border-t-brand-terracotta animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground text-sm flex flex-col items-center gap-2" data-testid="bitacora-empty">
            <ShieldCheck size={28} className="text-brand-sage" />
            {logs.length === 0 ? "Aún no hay registros de actividad." : "Sin coincidencias para tu búsqueda."}
          </div>
        ) : (
          <div className="divide-y divide-stone-100" data-testid="bitacora-list">
            {filtered.map((l) => {
              const m = ACTION_META[l.action] || { icon: Eye, chip: "text-stone-600 bg-stone-100", label: l.action };
              const Icon = m.icon;
              return (
                <div key={l.log_id} className="flex items-center gap-3 px-5 py-3.5 hover:bg-muted/30 transition-colors" data-testid={`log-row-${l.log_id}`}>
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${m.chip}`}><Icon size={16} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-brand-ink truncate">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full mr-2 ${m.chip}`}>{m.label}</span>
                      {l.entity}{l.detail ? <span className="text-muted-foreground font-normal"> · {l.detail}</span> : ""}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {l.doctor_name} · IP {l.ip || "—"}{l.patient_id ? ` · exp. ${l.patient_id}` : ""}
                    </p>
                  </div>
                  <span className="text-[11px] text-muted-foreground shrink-0 text-right">{new Date(l.at).toLocaleString("es-MX")}</span>
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
}
